import * as bitcoin from "bitcoinjs-lib";
import { BitcoinConnector } from "../../../../core/types/bitcoin";
import {
  BitcoinConnectorInvalidAccountError,
  BitcoinConnectorProviderNotFoundError,
  BitcoinConnectorTransactionFailedError,
} from "../errors";
import { broadcastTx, createSendBtcPsbt, fromHexString } from "../helpers";
const MAINNET = bitcoin.networks.bitcoin;

export interface PhantomBitcoinAccount {
  address: string;
  publicKey: string;
  addressType: "p2tr" | "p2wpkh" | "p2sh" | "p2pkh";
  purpose: "payment" | "ordinals";
}

interface PhantomProvider {
  requestAccounts: () => Promise<PhantomBitcoinAccount[]>;
  signPSBT: (
    psbt: Uint8Array,
    options: {
      inputsToSign: {
        address: string;
        signingIndexes: number[];
      }[];
    }
  ) => Promise<Uint8Array>;
}

export class PhantomConnector implements BitcoinConnector {
  public get getProvider(): PhantomProvider {
    const provider = (window as any)?.phantom?.bitcoin;

    if (!provider) {
      throw new BitcoinConnectorProviderNotFoundError("Phantom");
    }

    return provider;
  }

  async requestAccount() {
    const phantomAccounts = await this.getProvider.requestAccounts();

    if (!phantomAccounts) throw new BitcoinConnectorInvalidAccountError();

    const paymentAccount = phantomAccounts.find(
      (account: { purpose: string }) => account.purpose === "payment"
    );

    if (typeof paymentAccount?.address !== "string") {
      throw new BitcoinConnectorInvalidAccountError();
    }

    return {
      address: paymentAccount.address,
    };
  }

  async sendBTC(to: string, amount: number) {
    const { address } = await this.requestAccount();
    const { psbtHex } = await createSendBtcPsbt(address, to, amount);
    const { txHash } = await this.signPsbt(psbtHex);

    return { txHash };
  }

  async signPsbt(psbtHex: string): Promise<{
    txHash: string;
  }> {
    const { address: paymentAddress } = await this.requestAccount();

    const toSignPsbt = bitcoin.Psbt.fromHex(String(psbtHex), {
      network: MAINNET,
    });

    const inputs = toSignPsbt.data.inputs;
    const inputsToSign = [
      {
        // Need to sign each input with the payment address
        address: paymentAddress,
        signingIndexes: inputs.map((_, index) => index),
      },
    ];

    const signedPsbtBytes = await this.getProvider.signPSBT(
      fromHexString(psbtHex),
      {
        inputsToSign,
      }
    );

    const signedPsbt = bitcoin.Psbt.fromBuffer(signedPsbtBytes);

    signedPsbt.finalizeAllInputs();

    const tx = signedPsbt.extractTransaction();

    const txHash = await broadcastTx(tx.toHex());

    if (!txHash) throw new BitcoinConnectorTransactionFailedError();

    return { txHash };
  }
}
